/**
 * Injury Comparison Tab
 * Admin-only view that compares injury reports across BallDontLie, ESPN, and
 * MySportsFeeds for a given league so we can spot provider drift / disagreement.
 *
 * BDL and MSF are NBA-only at the provider level, so switching the league
 * dropdown to a non-NBA league effectively shows ESPN-only coverage.
 */

import { LoadingSpinner } from '@components/ui';
import { useAuth } from '@contexts/AuthContext';
import { useCurrentLeagueKey } from '@hooks/useCurrentLeagueKey';
import {
  type LeagueKey,
  leagueKeyToLeague,
  SUPPORTED_LEAGUE_KEYS,
} from '@parlay-picker/shared/league';
import { AlertTriangle, CheckCircle, RefreshCw, XCircle } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../../config/api';

// =============================================================================
// Types — mirror the response shape from
// server/routes/injuryComparisonRoutes.ts. Local to this admin tool; not
// shared across other consumers.
// =============================================================================

interface PlayerComparison {
  playerName: string;
  teamAbbr: string;
  bdl: { status: string; returnDate: string; description: string } | null;
  espn: { status: string; description: string } | null;
  msf: { status: string; description: string } | null;
  sourceCount: number;
  statusesAgree: boolean;
}

/**
 * Per-provider fetch outcome. Lets the UI distinguish the three "no data"
 * cases that all used to show "0":
 *   - 'queried'                 — provider was hit; count is the truth.
 *   - 'not_supported_for_league' — provider only supports a different league.
 *   - 'subscription_inactive'   — only MSF — account has no MSF subscription.
 *   - 'failed'                  — transient network/API failure.
 */
type BdlEspnStatus = 'queried' | 'not_supported_for_league' | 'failed';
type MsfStatus = BdlEspnStatus | 'subscription_inactive';

/**
 * `leagueKey` and `providerStatus` are typed optional because an older
 * server build doesn't send them. The component normalizes with safe
 * defaults so a stale-backend deploy or a local server that hasn't been
 * restarted doesn't crash the tab.
 */
interface ComparisonSummary {
  leagueKey?: LeagueKey;
  providerStatus?: { bdl: BdlEspnStatus; espn: BdlEspnStatus; msf: MsfStatus };
  counts: { bdl: number; espn: number; msf: number; union: number };
  coverage: { allThree: number; twoOfThree: number; oneOnly: number };
  statusBreakdown: {
    bdl: Record<string, number>;
    espn: Record<string, number>;
    msf: Record<string, number>;
  };
  players: PlayerComparison[];
  fetchTimesMs: { bdl: number; espn: number; msf: number };
}

/** Defaults when the server hasn't been redeployed with the new contract. */
const FALLBACK_PROVIDER_STATUS: {
  bdl: BdlEspnStatus;
  espn: BdlEspnStatus;
  msf: MsfStatus;
} = { bdl: 'queried', espn: 'queried', msf: 'queried' };

/** Per-state subtitle text on provider stat cards / status breakdown panels. */
function providerStateLabel(status: BdlEspnStatus | MsfStatus, timingMs: number): string {
  switch (status) {
    case 'queried':
      return timingMs >= 0 ? `${timingMs} ms` : 'Fetch failed';
    case 'not_supported_for_league':
      return 'Not supported for this league';
    case 'subscription_inactive':
      return 'Subscription inactive';
    case 'failed':
      return 'Fetch failed';
  }
}

// =============================================================================
// Sub-Components
// =============================================================================

function LeagueButtons({
  current,
  onChange,
}: {
  current: LeagueKey;
  onChange: (key: LeagueKey) => void;
}): ReactElement {
  return (
    <div className="flex flex-wrap gap-1.5">
      {[...SUPPORTED_LEAGUE_KEYS].map((key) => {
        const active = key === current;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all whitespace-nowrap ${
              active
                ? 'bg-theme-primary-fill text-theme-on-primary-fill ring-1 ring-theme-primary/50'
                : 'bg-theme-surface-hover text-theme-text-secondary hover:text-theme-text hover:bg-theme-border'
            }`}
          >
            {leagueKeyToLeague(key)}
          </button>
        );
      })}
    </div>
  );
}

function ProviderStatCard({
  label,
  value,
  status,
  timingMs,
}: {
  label: string;
  value: number;
  status: BdlEspnStatus | MsfStatus;
  timingMs: number;
}): ReactElement {
  return (
    <div className="bg-theme-bg-secondary rounded-lg p-4 border border-theme-border min-w-[140px]">
      <p className="text-sm text-theme-text-secondary mb-1">{label}</p>
      <p className="text-2xl font-bold text-theme-text">
        {status === 'queried' ? value.toLocaleString() : '—'}
      </p>
      <p className="text-xs text-theme-text-tertiary mt-1">
        {providerStateLabel(status, timingMs)}
      </p>
    </div>
  );
}

function StatusBreakdownTable({
  title,
  statuses,
  status,
}: {
  title: string;
  statuses: Record<string, number>;
  status: BdlEspnStatus | MsfStatus;
}): ReactElement {
  const entries = Object.entries(statuses).sort((a, b) => b[1] - a[1]);
  return (
    <div className="bg-theme-bg-secondary rounded-lg p-4 border border-theme-border">
      <h4 className="text-sm font-semibold text-theme-text mb-3">{title}</h4>
      {status !== 'queried' ? (
        <p className="text-xs text-theme-text-tertiary">{providerStateLabel(status, -1)}</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-theme-text-tertiary">No injuries reported</p>
      ) : (
        <ul className="space-y-1.5">
          {entries.map(([s, count]) => (
            <li key={s} className="flex items-center justify-between text-sm">
              <span className="text-theme-text-secondary">{s}</span>
              <span className="font-mono text-theme-text font-medium">{count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProviderCell({
  data,
  status,
}: {
  data: { status: string; description?: string } | null;
  status: BdlEspnStatus | MsfStatus;
}): ReactElement {
  if (status !== 'queried') {
    return <span className="text-xs text-theme-text-tertiary italic">n/a</span>;
  }
  if (!data) {
    return <span className="text-xs text-theme-text-tertiary">—</span>;
  }
  return (
    <div className="space-y-0.5">
      <p className="text-sm font-medium text-theme-text">{data.status}</p>
      {data.description && (
        <p className="text-xs text-theme-text-tertiary line-clamp-2">{data.description}</p>
      )}
    </div>
  );
}

function PlayerComparisonTable({
  players,
  providerStatus,
}: {
  players: PlayerComparison[];
  providerStatus: { bdl: BdlEspnStatus; espn: BdlEspnStatus; msf: MsfStatus };
}): ReactElement {
  if (players.length === 0) {
    return (
      <div className="bg-theme-bg-secondary rounded-lg p-6 text-center border border-theme-border">
        <p className="text-sm text-theme-text-secondary">
          No injury reports for this league across any provider.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-theme-bg-secondary rounded-lg border border-theme-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-theme-border">
            <th className="text-left py-3 px-4 text-theme-text-secondary font-medium">Player</th>
            <th className="text-left py-3 px-4 text-theme-text-secondary font-medium">Team</th>
            <th className="text-left py-3 px-4 text-theme-text-secondary font-medium">BDL</th>
            <th className="text-left py-3 px-4 text-theme-text-secondary font-medium">ESPN</th>
            <th className="text-left py-3 px-4 text-theme-text-secondary font-medium">MSF</th>
            <th className="text-center py-3 px-4 text-theme-text-secondary font-medium">Agree?</th>
          </tr>
        </thead>
        <tbody>
          {players.map((p) => (
            <tr
              key={`${p.playerName}-${p.teamAbbr}`}
              className="border-b border-theme-border last:border-b-0 align-top"
            >
              <td className="py-3 px-4 text-theme-text font-medium">{p.playerName}</td>
              <td className="py-3 px-4 text-theme-text-secondary font-mono">{p.teamAbbr}</td>
              <td className="py-3 px-4 max-w-[200px]">
                <ProviderCell data={p.bdl} status={providerStatus.bdl} />
              </td>
              <td className="py-3 px-4 max-w-[200px]">
                <ProviderCell data={p.espn} status={providerStatus.espn} />
              </td>
              <td className="py-3 px-4 max-w-[200px]">
                <ProviderCell data={p.msf} status={providerStatus.msf} />
              </td>
              <td className="py-3 px-4 text-center">
                {p.sourceCount <= 1 ? (
                  <span className="text-xs text-theme-text-tertiary">single source</span>
                ) : p.statusesAgree ? (
                  <CheckCircle className="w-4 h-4 text-theme-success inline-block" />
                ) : (
                  <XCircle className="w-4 h-4 text-theme-danger inline-block" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function InjuryComparisonTab(): ReactElement {
  const { authFetch } = useAuth();
  const currentLeagueKey = useCurrentLeagueKey();
  const [leagueKey, setLeagueKey] = useState<LeagueKey>(currentLeagueKey);
  const [data, setData] = useState<ComparisonSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (key: LeagueKey) => {
      setLoading(true);
      setError(null);

      try {
        const response = await authFetch(
          apiUrl(`/api/admin/injury-comparison?league=${encodeURIComponent(key)}`),
        );
        if (!response.ok) {
          const body = (await response.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `Request failed (${response.status})`);
        }
        const result = (await response.json()) as ComparisonSummary;
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  useEffect(() => {
    fetchData(leagueKey);
  }, [fetchData, leagueKey]);

  // Normalize optional fields so the tab tolerates the pre-providerStatus
  // server response (rolling deploy / stale local backend safety).
  const providerStatus = data?.providerStatus ?? FALLBACK_PROVIDER_STATUS;

  return (
    <div className="space-y-6 pb-12">
      {/* Header — centered to match AIAccuracy / PicksData pattern */}
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-theme-text">Injury Provider Comparison</h2>
          <p className="text-sm text-theme-text-secondary max-w-2xl mx-auto">
            Side-by-side view of BallDontLie, ESPN, and MySportsFeeds injury reports. BDL and MSF
            are NBA-only; non-NBA leagues fall through to ESPN.
          </p>
        </div>
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => fetchData(leagueKey)}
            disabled={loading}
            className="px-4 py-2 bg-theme-primary-fill text-theme-on-primary-fill rounded-lg hover:bg-theme-primary-fill-hover transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <LeagueButtons current={leagueKey} onChange={setLeagueKey} />
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center p-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="bg-theme-bg-secondary rounded-lg p-6 border border-theme-danger/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-theme-danger mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-theme-danger font-medium">Failed to load comparison</p>
              <p className="text-sm text-theme-text-secondary mt-1">{error}</p>
              <button
                type="button"
                onClick={() => fetchData(leagueKey)}
                className="mt-3 px-3 py-1.5 bg-theme-primary-fill text-theme-on-primary-fill rounded text-sm hover:bg-theme-primary-fill-hover transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Data */}
      {!loading && !error && data && (
        <>
          {/* Counts */}
          <div className="flex flex-wrap gap-4">
            <ProviderStatCard
              label="BallDontLie"
              value={data.counts.bdl}
              status={providerStatus.bdl}
              timingMs={data.fetchTimesMs.bdl}
            />
            <ProviderStatCard
              label="ESPN"
              value={data.counts.espn}
              status={providerStatus.espn}
              timingMs={data.fetchTimesMs.espn}
            />
            <ProviderStatCard
              label="MySportsFeeds"
              value={data.counts.msf}
              status={providerStatus.msf}
              timingMs={data.fetchTimesMs.msf}
            />
            <div className="bg-theme-bg-secondary rounded-lg p-4 border border-theme-border min-w-[140px]">
              <p className="text-sm text-theme-text-secondary mb-1">Union</p>
              <p className="text-2xl font-bold text-theme-text">
                {data.counts.union.toLocaleString()}
              </p>
              <p className="text-xs text-theme-text-tertiary mt-1">
                Unique players across all sources
              </p>
            </div>
          </div>

          {/* Coverage breakdown */}
          <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
            <h3 className="text-lg font-semibold text-theme-text mb-4">Coverage Breakdown</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-theme-success">{data.coverage.allThree}</p>
                <p className="text-xs text-theme-text-secondary mt-1">All three providers</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-theme-warning">{data.coverage.twoOfThree}</p>
                <p className="text-xs text-theme-text-secondary mt-1">Two of three</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-theme-danger">{data.coverage.oneOnly}</p>
                <p className="text-xs text-theme-text-secondary mt-1">Only one source</p>
              </div>
            </div>
          </div>

          {/* Status breakdowns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatusBreakdownTable
              title="BDL Statuses"
              statuses={data.statusBreakdown.bdl}
              status={providerStatus.bdl}
            />
            <StatusBreakdownTable
              title="ESPN Statuses"
              statuses={data.statusBreakdown.espn}
              status={providerStatus.espn}
            />
            <StatusBreakdownTable
              title="MSF Statuses"
              statuses={data.statusBreakdown.msf}
              status={providerStatus.msf}
            />
          </div>

          {/* Player comparison */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-theme-text">Per-Player Comparison</h3>
            <PlayerComparisonTable players={data.players} providerStatus={providerStatus} />
          </div>
        </>
      )}
    </div>
  );
}
